/**
 * CR-7732: how a haulier change reads in the call-off's audit timeline.
 *
 * The entry's first field change names the old and new haulier. For the
 * customer, the rest are the previous haulier's cleared entries — kept in the
 * history rather than deleted, because past disputes were over prices agreed
 * outside the portal.
 */
import type { CallOffEditHistoryEntry, CallOffFieldChange } from '../../../lib/api';
import { truckTypeLabel } from './truckType';

const HAULAGE_FIELD_LABELS: Record<string, string> = {
  haulage_cost: 'Haulage cost',
  haulier_pickup_date: 'Pickup date',
  haulier_agreed_delivery_date: 'Agreed delivery date',
  lorry_number: 'Lorry number',
  truck_type: 'Truck type',  // CR-9072
  // CR-7781: a haulier change drops the previous haulier's Confirm Load too.
  logistics_confirmed_at: 'Logistics Confirmed',
};

const DATE_FIELDS = new Set([
  'haulier_pickup_date', 'haulier_agreed_delivery_date', 'logistics_confirmed_at',
]);

/**
 * A haulage value as the timeline reads it: dates in the viewer's format,
 * a truck type by its label (CR-9072), anything else as stored.
 *
 * @param field - the field the value belongs to
 * @param value - the stored value
 * @param formatDate - the user's date formatter
 * @returns the text, or '' for none
 */
export function haulageValueText(
  field: string,
  value: string | null | undefined,
  formatDate: (date: string | Date) => string,
): string {
  if (!value) return '';
  if (DATE_FIELDS.has(field)) return formatDate(value);
  if (field === 'truck_type') return truckTypeLabel(value);
  return value;
}

/**
 * The one timeline entry for a haulage save.
 *
 * A haulier fills its figures in together and saves once, so the audit says
 * so once. Rendering a line per value turned a single save into four entries
 * and pushed the rest of the history out of view — the record of what
 * happened should be the same shape as what happened.
 *
 * Returns null when the viewer may not see the values: the supplier's copy of
 * the entry carries only the fields it is allowed (the pickup date), and a
 * copy with nothing left in it is dropped rather than drawn empty.
 *
 * @param entry - a `haulage_entered` edit-history entry
 * @param formatDate - the user's date formatter
 * @returns the action and the values it set, or null
 */
export function haulageEntryLine(
  entry: CallOffEditHistoryEntry,
  formatDate: (date: string | Date) => string,
): { action: string; detail: string } | null {
  const changes = entry.field_changes || [];
  if (changes.length === 0) return null;

  const format = (field: string, value: string | null) => haulageValueText(field, value, formatDate);

  const parts = changes.map(change => {
    const label = HAULAGE_FIELD_LABELS[change.field] || change.field.replace(/_/g, ' ');
    const to = format(change.field, change.new) || 'cleared';
    // Only say what it was when there was something there before.
    return change.old
      ? `${label} ${format(change.field, change.old)} → ${to}`
      : `${label} ${to}`;
  });

  const anyChanged = changes.some(change => Boolean(change.old));
  return {
    action: anyChanged ? 'Changed haulage details' : 'Entered haulage details',
    detail: parts.join(' · '),
  };
}

/**
 * CR-7781: the timeline sentence for a cost the customer changed itself. Its
 * own wording, so a figure the customer typed never reads as the haulier's.
 *
 * @param change - the entry's `haulage_cost` field change
 * @returns e.g. "Haulage cost changed by customer"
 */
/** CR-8613: what each value the customer may change is called in the history. */
export const CUSTOMER_HAULAGE_LABELS: Record<string, string> = {
  pickup_date: 'Expected pickup date',
  haulage_cost: 'Haulage cost',
  haulier_pickup_date: 'Pickup date',
  haulier_agreed_delivery_date: 'Agreed delivery date',
  lorry_number: 'Lorry number',
  truck_type: 'Truck type',  // CR-9072
};

export function customerCostAction(change: CallOffFieldChange): string {
  // CR-8613: every value is changed the same way, logged the same way.
  const what = CUSTOMER_HAULAGE_LABELS[change.field] ?? 'Haulage cost';
  if (change.old && change.new) return `${what} changed by customer`;
  if (change.new) return `${what} entered by customer`;
  return `${what} removed by customer`;
}

/**
 * The sentence for a haulier change, e.g. "Haulier changed from Scat to Kowalski".
 *
 * @param change - the entry's `haulier` field change
 * @returns the timeline action text
 */
export function haulierChangeAction(change: CallOffFieldChange | undefined): string {
  const from = change?.old;
  const to = change?.new;
  if (from && to) return `Haulier changed from ${from} to ${to}`;
  if (to) return `Haulier set to ${to}`;
  if (from) return `Haulier ${from} removed`;
  return 'Haulier changed';
}

/**
 * The previous haulier's entries that the change cleared, as one line — or
 * undefined when there were none (or the viewer may not see them).
 *
 * @param entry - a `haulier_changed` edit-history entry
 * @param formatDate - the user's date formatter
 * @returns e.g. "Previous haulier's entries: Haulage cost 450.00 · Lorry number PO 12345"
 */
export function haulierPreviousEntries(
  entry: CallOffEditHistoryEntry,
  formatDate: (date: string | Date) => string,
): string | undefined {
  const parts = (entry.field_changes || [])
    .filter(change => change.field !== 'haulier' && change.old)
    .map(change => {
      const label = HAULAGE_FIELD_LABELS[change.field] || change.field.replace(/_/g, ' ');
      return `${label} ${haulageValueText(change.field, change.old, formatDate)}`;
    });
  return parts.length ? `Previous haulier's entries: ${parts.join(' · ')}` : undefined;
}
